// Componente MegaButton - Botón principal personalizado
// Reutiliza la lógica y estética de amarsi-app para la web

import React from 'react';
import { FiLoader } from 'react-icons/fi';

/**
 * Componente MegaButton
 * 
 * Botón principal con estados de carga y deshabilitado
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.labelText - Texto o contenido del botón
 * @param {Function} props.onPress - Función al hacer clic
 * @param {boolean} props.loading - Si está en estado de carga
 * @param {boolean} props.disabled - Si está deshabilitado
 * @param {string} props.customButton - Clases CSS personalizadas
 * @param {string} props.customButtonText - Clases CSS para el texto
 * @param {string} props.className - Clases adicionales
 * @param {string} props.size - Tamaño del botón ('sm', 'md', 'lg')
 * @returns {JSX.Element} Botón personalizado
 */
const MegaButton = ({ 
  labelText = 'Botón',
  onPress = () => {},
  loading = false,
  disabled = false,
  customButton = '',
  customButtonText = '',
  className = '',
  size = 'md'
}) => {
  // Definir tamaños base
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };

  // Clases base del botón
  const baseClasses = `
    relative overflow-hidden rounded-xl font-semibold transition-all duration-200
    flex items-center justify-center
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeClasses[size] || sizeClasses.md}
  `;

  // Clases por defecto si no se especifican
  const defaultButtonClasses = 'bg-primary-600 hover:bg-primary-700 text-white';
  const defaultTextClasses = 'text-white';

  // Combinar clases
  const buttonClasses = `
    ${baseClasses}
    ${customButton || defaultButtonClasses}
    ${className}
  `;

  const textClasses = customButtonText || defaultTextClasses;

  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled || loading}
      className={buttonClasses}
    >
      {/* Indicador de carga */}
      {loading && (
        <FiLoader className="w-4 h-4 animate-spin" />
      )}
      
      {/* Contenido del botón */}
      <span className={textClasses}>
        {loading ? 'Procesando...' : labelText}
      </span>
      
      {/* Efecto de onda opcional */}
      {!loading && !disabled && (
        <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-200" />
      )}
    </button>
  );
};

export default MegaButton;
